import { TWO_FACTOR_LABEL, TWO_FACTOR_TONE } from '@qlk/shared';

import {
  type CotBang,
  DataTable,
  EmptyState,
  ngayDay,
  Nut,
  StatusBadge,
} from '../../../ui/index.js';
import type { TaiKhoanGoogleDong } from '../api.js';

function ngayVn(iso: string | null): string {
  if (!iso) return 'Chưa rà soát';
  return ngayDay(iso);
}

export interface BangTaiKhoanGoogleProps {
  dong: TaiKhoanGoogleDong[];
  dangTai: boolean;
  coSua: boolean;
  onSua?: (ban: TaiKhoanGoogleDong) => void;
  onThem?: () => void;
}

export function BangTaiKhoanGoogle({
  dong,
  dangTai,
  coSua,
  onSua,
  onThem,
}: BangTaiKhoanGoogleProps) {
  const cot: CotBang<TaiKhoanGoogleDong>[] = [
    {
      key: 'label',
      header: 'Tài khoản Google',
      width: '220px',
      render: (d) => <span className="font-medium text-chu">{d.label}</span>,
    },
    {
      key: 'kenh',
      header: 'Kênh quản lý',
      render: (d) => (
        <span className="text-chu-phu">
          {d.channelNames.length > 0 ? d.channelNames.join(', ') : '—'}
        </span>
      ),
    },
    {
      key: 'ketNoi',
      header: 'Còn kết nối',
      align: 'right',
      render: (d) => (
        <span className="so-lieu">
          {d.connectedChannels}/{d.channelIds.length}
        </span>
      ),
    },
    {
      key: '2fa',
      header: '2FA',
      render: (d) => (
        <StatusBadge tone={TWO_FACTOR_TONE[d.twoFactorMethod]}>
          {TWO_FACTOR_LABEL[d.twoFactorMethod]}
        </StatusBadge>
      ),
    },
    {
      key: 'khoiPhuc',
      header: 'Email khôi phục',
      render: (d) => (
        <StatusBadge tone={d.recoveryEmailSet ? 'ok' : 'warn'} cham={false}>
          {d.recoveryEmailSet ? 'Đã đặt' : 'Chưa đặt'}
        </StatusBadge>
      ),
    },
    {
      key: 'raSoat',
      header: 'Rà soát gần nhất',
      render: (d) => (
        <span className="flex flex-wrap items-center gap-2">
          <span className="so-lieu text-chu-phu">{ngayVn(d.lastReviewedAt)}</span>
          {d.needsReview && <StatusBadge tone="warn">Quá 90 ngày</StatusBadge>}
        </span>
      ),
    },
    {
      key: 'ghiChu',
      header: 'Ghi chú nghi vấn',
      render: (d) =>
        d.suspiciousLoginNote ? (
          <span className="text-bad-chu">{d.suspiciousLoginNote}</span>
        ) : (
          <span className="text-chu-mo">—</span>
        ),
    },
    ...(coSua
      ? [
          {
            key: 'thaoTac',
            header: '',
            align: 'right' as const,
            render: (d: TaiKhoanGoogleDong) => (
              <Nut
                kieu="nhe"
                className="h-9 min-h-0 px-2 text-phu"
                onClick={() => {
                  onSua?.(d);
                }}
              >
                Sửa
              </Nut>
            ),
          },
        ]
      : []),
  ];

  return (
    <section className="mt-6">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-tieu-de-hop font-bold text-chu">Tài khoản Google quản lý kênh</h2>
          <p className="mt-1 text-co-ban text-chu-phu">
            Chỉ lưu trạng thái bảo mật. Không lưu mật khẩu, mã khôi phục hay mã 2FA.
          </p>
        </div>
        {coSua && (
          <Nut kieu="phu" onClick={onThem}>
            + Thêm tài khoản
          </Nut>
        )}
      </header>

      <DataTable
        columns={cot}
        rows={dong}
        keyOf={(d) => d.id}
        dangTai={dangTai}
        caption="Sổ theo dõi bảo mật tài khoản Google quản lý kênh"
        khiRong={
          <EmptyState
            title="Chưa có tài khoản nào trong sổ"
            description="Thêm tài khoản Google (email đã che) để theo dõi tình trạng bảo mật."
          />
        }
      />
    </section>
  );
}
